import { useState, useRef, useEffect, useMemo } from 'react';
import type { AxiosError } from 'axios';
import { Filter as FilterIcon, Map as MapIcon } from 'lucide-react';
import { VolcanoMap, LayerControls, ViewportControls, SampleDetailsPanel, SummaryStats, ChartPanel, SelectionOverlay } from '../components/Map';
import { BboxSearchWidget } from '../components/Map/BboxSearchWidget';
import { FilterPanel } from '../components/Filters';
import { SelectionToolbar } from '../components/Selection';
import { useVolcanoes } from '../hooks/useVolcanoes';
import { useTectonic } from '../hooks/useTectonic';
import { useSelectionStore } from '../store';
import { Loader } from '../components/Common/Loader';
import { ErrorMessage } from '../components/Common/ErrorMessage';
import { exportSamplesToCSV } from '../utils/csvExport';
import { filterSamplesByMethod } from '../utils/matchMethod';
import { useKeyboardShortcuts, commonShortcuts } from '../hooks/useKeyboardShortcuts';
import { formatBboxForAPI } from '../hooks/useBboxDraw';
import { fetchSamples } from '../api/samples';
import { normalizeBbox, splitBboxAtAntimeridian } from '../utils/longitude';
import type {
  BBox,
  ComparisonVolcanoOption,
  RockTypeComparisonMode,
  Sample,
  SampleFilters,
  Volcano,
  VolcanoFilters,
} from '../types';
import type { MatchMethod } from '../utils/matchMethod';
import { BOUNDARY_TYPES, type BoundaryType } from '../components/Map/mapStyle';

const INITIAL_VIEWPORT = {
  longitude: 0,
  latitude: 20,
  zoom: 2,
};

type BboxDrawMode = 'none' | 'primary' | 'comparison';

interface APIErrorResponse {
  detail?: string;
}

interface MapInteractionInfo {
  coordinate?: number[];
}

const buildRadarComparisonFilters = (
  sampleFilters: SampleFilters
): SampleFilters => ({
  database: sampleFilters.database,
  rock_type: sampleFilters.rock_type,
  tectonic_setting: sampleFilters.tectonic_setting,
  min_sio2: sampleFilters.min_sio2,
  max_sio2: sampleFilters.max_sio2,
  material: 'WR',
});

/**
 * Fetch the samples inside a bbox. A bbox crossing the antimeridian is sent as
 * two requests (the API only accepts longitudes within [-180, 180]).
 */
const fetchSamplesInBbox = async (bbox: BBox, filters: SampleFilters): Promise<Sample[]> => {
  const responses = await Promise.all(
    splitBboxAtAntimeridian(bbox).map((part) => fetchSamples({ ...filters, bbox: formatBboxForAPI(part) }))
  );
  if (responses.length === 1) return responses[0].data;

  // Points lying exactly on the antimeridian may be returned by both requests
  const seen = new Set<string>();
  return responses.flatMap((response) => response.data).filter((sample) => {
    if (seen.has(sample._id)) return false;
    seen.add(sample._id);
    return true;
  });
};

const MapPage = () => {
  // Layer visibility state
  const [showVolcanoes, setShowVolcanoes] = useState(true);
  const [showSamplePoints, setShowSamplePoints] = useState(true);
  // Plate boundary types shown on the map (all by default)
  const [visibleBoundaryTypes, setVisibleBoundaryTypes] = useState<BoundaryType[]>(() => BOUNDARY_TYPES.map((b) => b.type));

  // Viewport state
  const [viewport, setViewport] = useState(INITIAL_VIEWPORT);

  // Filter state
  const [sampleFilters, setSampleFilters] = useState<SampleFilters>({}); // Start with no filters - user must apply filters or bbox
  const [volcanoFilters, setVolcanoFilters] = useState<VolcanoFilters>({}); // Increased default limit
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [hasAppliedFilters, setHasAppliedFilters] = useState(false); // Track if user has applied any filters
  const [showNoSamplesMessage, setShowNoSamplesMessage] = useState(true); // Control visibility of "No Samples" popup
  
  // Separate state for volcano samples and bbox samples (for OR logic)
  const [volcanoSamples, setVolcanoSamples] = useState<Sample[]>([]);
  const [bboxSamples, setBboxSamples] = useState<Sample[]>([]);
  const [loadingVolcanoSamples, setLoadingVolcanoSamples] = useState(false);
  const [loadingBboxSamples, setLoadingBboxSamples] = useState(false);
  const [volcanoSamplesError, setVolcanoSamplesError] = useState<string | null>(null);
  const [bboxSamplesError, setBboxSamplesError] = useState<string | null>(null);

  // Chart panel state
  const [chartPanelOpen, setChartPanelOpen] = useState(false);
  const [selectedMatchMethods, setSelectedMatchMethods] = useState<MatchMethod[]>(['literature', 'nearest', 'no_match']);
  const [comparisonMode, setComparisonMode] = useState<RockTypeComparisonMode>('none');
  const [comparisonVolcano, setComparisonVolcano] = useState<ComparisonVolcanoOption | null>(null);
  const [comparisonSamples, setComparisonSamples] = useState<Sample[]>([]);
  const [loadingComparisonSamples, setLoadingComparisonSamples] = useState(false);
  const [comparisonSamplesError, setComparisonSamplesError] = useState<string | null>(null);

  // Bbox state
  const [currentBbox, setCurrentBbox] = useState<BBox | null>(null);
  const [comparisonBbox, setComparisonBbox] = useState<BBox | null>(null);
  const [bboxDrawMode, setBboxDrawMode] = useState<BboxDrawMode>('none');
  const [totalSamplesCount, setTotalSamplesCount] = useState<number | undefined>(undefined);

  const isDrawingBbox = bboxDrawMode !== 'none';
  const isDrawingComparisonBbox = bboxDrawMode === 'comparison';

  // Selection state - using individual selectors for better reactivity
  const selectedSamples = useSelectionStore((state) => state.selectedSamples);
  const selectionMode = useSelectionStore((state) => state.selectionMode);
  const setSelectionMode = useSelectionStore((state) => state.setSelectionMode);
  const clearSelection = useSelectionStore((state) => state.clearSelection);
  const addSelectedSamples = useSelectionStore((state) => state.addSelectedSamples);

  // Map container ref and dimensions for SelectionOverlay
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const [mapDimensions, setMapDimensions] = useState({ width: 0, height: 0 });

  // Track map container dimensions
  useEffect(() => {
    const updateDimensions = () => {
      if (mapContainerRef.current) {
        setMapDimensions({
          width: mapContainerRef.current.clientWidth,
          height: mapContainerRef.current.clientHeight,
        });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  // Sample details state (for details panel)
  const [clickedSample, setClickedSample] = useState<Sample | null>(null);

  // Fetch data using custom hooks (with filters)
  const { volcanoes, loading: volcanoesLoading, error: volcanoesError } = useVolcanoes(volcanoFilters);
  const { boundaries, loading: tectonicLoading, error: tectonicError } = useTectonic();
  
  // Merge volcano samples and bbox samples (OR logic)
  const samples = useMemo(() => {
    // If we have both, merge and deduplicate by sample _id
    if (volcanoSamples.length > 0 && bboxSamples.length > 0) {
      const sampleMap = new Map<string, Sample>();
      
      // Add volcano samples first
      volcanoSamples.forEach(sample => {
        if (sample._id) sampleMap.set(sample._id, sample);
      });
      
      // Add bbox samples (won't overwrite duplicates)
      bboxSamples.forEach(sample => {
        if (sample._id && !sampleMap.has(sample._id)) {
          sampleMap.set(sample._id, sample);
        }
      });
      
      return Array.from(sampleMap.values());
    }
    
    // If only one source has samples, return that
    if (volcanoSamples.length > 0) return volcanoSamples;
    if (bboxSamples.length > 0) return bboxSamples;
    
    return [];
  }, [volcanoSamples, bboxSamples]);
  
  const samplesLoading = loadingVolcanoSamples || loadingBboxSamples;

  // Convert boundaries response to array
  const tectonicBoundaries = useMemo(
    () => (boundaries?.features || []).filter((b) => visibleBoundaryTypes.includes(b.properties?.boundary_type)),
    [boundaries, visibleBoundaryTypes],
  );

  const selectedVolcano = useMemo(() => {
    const selectedVolcanoNumber = volcanoFilters.volcano_number;

    if (selectedVolcanoNumber !== undefined && volcanoes.length > 0) {
      return volcanoes.find((volcano) => volcano.volcano_number === selectedVolcanoNumber)
        ?? (volcanoes.length === 1 ? volcanoes[0] : null);
    }

    const selectedVolcanoName = volcanoFilters.volcano_name?.trim().toLowerCase();

    if (!selectedVolcanoName || volcanoes.length === 0) {
      return null;
    }

    return volcanoes.find((volcano) => volcano.volcano_name.trim().toLowerCase() === selectedVolcanoName)
      ?? (volcanoes.length === 1 ? volcanoes[0] : null);
  }, [volcanoFilters.volcano_name, volcanoFilters.volcano_number, volcanoes]);

  // Fetch samples for selected volcano
  useEffect(() => {
    const fetchVolcanoSamples = async () => {
      if (selectedVolcano) {
        // Zoom to volcano
        if (selectedVolcano.geometry?.coordinates) {
          const [longitude, latitude] = selectedVolcano.geometry.coordinates;
          setViewport({
            longitude,
            latitude,
            zoom: 8, // Close zoom to see volcano and samples
          });
        }

        // Fetch samples for this volcano
        if (selectedVolcano.volcano_number) {
          setLoadingVolcanoSamples(true);
          setHasAppliedFilters(true);
          setVolcanoSamplesError(null); // Clear previous errors

          try {
            // Combine volcano_number with sampleFilters (database, rock_type, tectonic_setting, SiO2)
            const response = await fetchSamples({
              volcano_number: selectedVolcano.volcano_number,
              ...sampleFilters, // Include all sample filters (database, rock_type, tectonic_setting, min_sio2, max_sio2)
            });
            setVolcanoSamples(response.data);
            setVolcanoSamplesError(null);
          } catch (error: unknown) {
            console.error('Error fetching volcano samples:', error);
            const axiosError = error as AxiosError<APIErrorResponse>;
            const errorMessage = axiosError.response?.data?.detail || axiosError.message || 'Failed to fetch volcano samples';
            setVolcanoSamplesError(errorMessage);
            setVolcanoSamples([]);
          } finally {
            setLoadingVolcanoSamples(false);
          }
        }
      } else {
        // Clear volcano samples if no volcano selected
        setVolcanoSamples([]);
        setVolcanoSamplesError(null);
      }
    };
    
    fetchVolcanoSamples();
  }, [selectedVolcano, sampleFilters]);

  // Fetch samples within bbox
  useEffect(() => {
    const fetchBboxSamples = async () => {
      if (currentBbox) {
        setLoadingBboxSamples(true);
        setHasAppliedFilters(true);
        setBboxSamplesError(null); // Clear previous errors
        
        try {
          // Combine bbox with sampleFilters (database, rock_type, tectonic_setting, SiO2)
          const bboxSamples = await fetchSamplesInBbox(currentBbox, {
            ...sampleFilters, // Include all sample filters (database, rock_type, tectonic_setting, min_sio2, max_sio2)
          });
          setBboxSamples(bboxSamples);
          setBboxSamplesError(null);
        } catch (error: unknown) {
          console.error('Error fetching bbox samples:', error);
          const axiosError = error as AxiosError<APIErrorResponse>;
          const errorMessage = axiosError.response?.data?.detail || axiosError.message || 'Failed to fetch samples in bounding box';
          setBboxSamplesError(errorMessage);
          setBboxSamples([]);
        } finally {
          setLoadingBboxSamples(false);
        }
      } else {
        // Clear bbox samples if no bbox selected
        setBboxSamples([]);
        setBboxSamplesError(null);
      }
    };
    
    fetchBboxSamples();
  }, [currentBbox, sampleFilters]);

  useEffect(() => {
    const fetchComparisonSamples = async () => {
      if (comparisonMode === 'none' || comparisonMode === 'global') {
        setComparisonSamples([]);
        setComparisonSamplesError(null);
        setLoadingComparisonSamples(false);
        return;
      }

      if (comparisonMode === 'volcano' && !comparisonVolcano) {
        setComparisonSamples([]);
        setComparisonSamplesError(null);
        setLoadingComparisonSamples(false);
        return;
      }

      if (comparisonMode === 'bbox' && !comparisonBbox) {
        setComparisonSamples([]);
        setComparisonSamplesError(null);
        setLoadingComparisonSamples(false);
        return;
      }

      setLoadingComparisonSamples(true);
      setComparisonSamplesError(null);

      try {
        const comparisonFilters = {
          ...buildRadarComparisonFilters(sampleFilters),
          ...(comparisonMode === 'volcano' && comparisonVolcano
            ? { volcano_number: comparisonVolcano.volcano_number }
            : {}),
        };
        const comparisonData = comparisonMode === 'bbox' && comparisonBbox
          ? await fetchSamplesInBbox(comparisonBbox, comparisonFilters)
          : (await fetchSamples(comparisonFilters)).data;

        setComparisonSamples(comparisonData);
        setComparisonSamplesError(null);
      } catch (error: unknown) {
        console.error('Error fetching comparison samples:', error);
        const axiosError = error as AxiosError<APIErrorResponse>;
        const errorMessage = axiosError.response?.data?.detail || axiosError.message || 'Failed to fetch comparison samples';
        setComparisonSamplesError(errorMessage);
        setComparisonSamples([]);
      } finally {
        setLoadingComparisonSamples(false);
      }
    };

    fetchComparisonSamples();
  }, [comparisonBbox, comparisonMode, comparisonVolcano, sampleFilters]);
  
  // Detect when user applies filters from FilterPanel (excluding just limit changes)
  // Also detect when filters are CLEARED (empty object) to prevent unnecessary fetches
  useEffect(() => {
    const hasNonLimitFilters = Object.keys(sampleFilters).some(
      key => key !== 'limit' && key !== 'offset' && key !== 'bbox' && key !== 'volcano_number'
    );
    
    if (hasNonLimitFilters && !hasAppliedFilters) {
      // User applied filters - allow fetching
      setHasAppliedFilters(true);
    } else if (!hasNonLimitFilters && hasAppliedFilters && !currentBbox && !volcanoFilters.volcano_name && volcanoFilters.volcano_number === undefined) {
      // Filters were cleared AND no bbox/volcano - prevent fetching
      setHasAppliedFilters(false);
    }
  }, [sampleFilters, hasAppliedFilters, currentBbox, volcanoFilters.volcano_name, volcanoFilters.volcano_number]);

  // Loading state
  const isLoading = samplesLoading || volcanoesLoading || tectonicLoading;
  
  // Combine all errors for display
  const allErrors = [volcanoesError, tectonicError, volcanoSamplesError, bboxSamplesError, comparisonSamplesError]
    .filter(Boolean)
    .join(' | ');
  const error = allErrors || null;
  const [dismissedError, setDismissedError] = useState<string | null>(null);

  // Handle volcano click
  const handleVolcanoClick = (volcano: Volcano) => {
    console.log('Volcano clicked:', volcano);
    // Future: Show volcano details in a sidebar or modal
  };

  const chartScopeSamples = selectedSamples.length > 0 ? selectedSamples : samples;

  const chartFilteredSamples = useMemo(() => {
    return filterSamplesByMethod(chartScopeSamples, selectedMatchMethods);
  }, [chartScopeSamples, selectedMatchMethods]);

  const filteredPrimarySamples = useMemo(() => {
    return filterSamplesByMethod(samples, selectedMatchMethods);
  }, [samples, selectedMatchMethods]);

  const visibleSelectedSamples = useMemo(() => {
    return filterSamplesByMethod(selectedSamples, selectedMatchMethods);
  }, [selectedSamples, selectedMatchMethods]);

  const filteredComparisonSamples = useMemo(() => {
    return filterSamplesByMethod(comparisonSamples, selectedMatchMethods);
  }, [comparisonSamples, selectedMatchMethods]);

  const downloadableSamples = chartFilteredSamples;

  // Download CSV handler
  const handleDownloadCSV = () => {
    exportSamplesToCSV(downloadableSamples);
  };

  // Keyboard shortcuts
  useKeyboardShortcuts([
    commonShortcuts.download(handleDownloadCSV),
  ]);

  // Handle sample click
  const handleSampleClick = (sample: Sample) => {
    setClickedSample(sample);
  };

  // Handle adding sample to selection
  const handleAddToSelection = (sample: Sample) => {
    // Check if sample is already in selection
    const isAlreadySelected = selectedSamples.some(s => s._id === sample._id);
    if (!isAlreadySelected) {
      addSelectedSamples([sample]);
    }
  };

  // Handle viewport changes from map
  const handleViewportChange = (newViewport: { longitude: number; latitude: number; zoom: number }) => {
    setViewport({
      longitude: newViewport.longitude,
      latitude: newViewport.latitude,
      zoom: newViewport.zoom,
    });
  };

  // Zoom controls
  const handleZoomIn = () => {
    setViewport((prev) => ({ ...prev, zoom: prev.zoom + 1 }));
  };

  const handleZoomOut = () => {
    setViewport((prev) => ({ ...prev, zoom: Math.max(0, prev.zoom - 1) }));
  };

  const handleResetView = () => {
    setViewport(INITIAL_VIEWPORT);
  };

  // Handle selection overlay completion
  const handleSelectionComplete = (newSelectedSamples: Sample[]) => {
    addSelectedSamples(newSelectedSamples);
    setSelectionMode('none');
  };

  // Handle selection cancel
  const handleSelectionCancel = () => {
    setSelectionMode('none');
  };

  // Bbox drawing state
  const [drawingStart, setDrawingStart] = useState<{ lon: number; lat: number } | null>(null);
  const [drawingCurrent, setDrawingCurrent] = useState<{ lon: number; lat: number } | null>(null);

  const resetDrawingState = () => {
    setBboxDrawMode('none');
    setDrawingStart(null);
    setDrawingCurrent(null);
  };

  // Bbox handlers
  const handleStartDrawing = () => {
    setBboxDrawMode('primary');
    setDrawingStart(null);
    setDrawingCurrent(null);
    // Clear any existing selection mode to avoid conflicts
    if (selectionMode !== 'none') {
      setSelectionMode('none');
    }
  };

  const handleStartComparisonBbox = () => {
    setBboxDrawMode('comparison');
    setDrawingStart(null);
    setDrawingCurrent(null);
    if (selectionMode !== 'none') {
      setSelectionMode('none');
    }
  };

  const handleMapClick = (info: MapInteractionInfo) => {
    if (!isDrawingBbox) return;

    const { coordinate } = info;
    if (!coordinate) return;

    const [lon, lat] = coordinate;

    if (!drawingStart) {
      // First click - set start point
      setDrawingStart({ lon, lat });
    } else {
      // Second click - complete the bbox
      const minLon = Math.min(drawingStart.lon, lon);
      const maxLon = Math.max(drawingStart.lon, lon);
      const minLat = Math.min(drawingStart.lat, lat);
      const maxLat = Math.max(drawingStart.lat, lat);

      // Validate minimum size (at least 0.1 degrees)
      if (Math.abs(maxLon - minLon) > 0.1 && Math.abs(maxLat - minLat) > 0.1) {
        const newBbox = normalizeBbox({ minLon, minLat, maxLon, maxLat });
        if (bboxDrawMode === 'comparison') {
          setComparisonBbox(newBbox);
        } else {
          setCurrentBbox(newBbox); // This will trigger bbox sample fetch via useEffect
        }
      }

      // Reset drawing state
      resetDrawingState();
    }
  };

  const handleMapHover = (info: MapInteractionInfo) => {
    if (!isDrawingBbox || !drawingStart) return;

    const { coordinate } = info;
    if (!coordinate) return;

    const [lon, lat] = coordinate;
    setDrawingCurrent({ lon, lat });
  };

  const handleSetPresetRegion = (bbox: BBox) => {
    setCurrentBbox(bbox); // This will trigger bbox sample fetch via useEffect
  };

  const handleClearBbox = () => {
    setCurrentBbox(null); // This will clear bbox samples via useEffect
    if (bboxDrawMode === 'primary') {
      resetDrawingState();
    }
  };

  const handleClearComparisonBbox = () => {
    setComparisonBbox(null);
    if (bboxDrawMode === 'comparison') {
      resetDrawingState();
    }
  };

  // Calculate drawing rectangle for visualization
  const getDrawingBbox = (): BBox | null => {
    if (!drawingStart || !drawingCurrent) return null;
    
    return {
      minLon: Math.min(drawingStart.lon, drawingCurrent.lon),
      maxLon: Math.max(drawingStart.lon, drawingCurrent.lon),
      minLat: Math.min(drawingStart.lat, drawingCurrent.lat),
      maxLat: Math.max(drawingStart.lat, drawingCurrent.lat),
    };
  };

  // Track total sample count when bbox is applied
  useEffect(() => {
    setTotalSamplesCount(samples.length);
  }, [samples]);

  // ESC key to cancel bbox drawing
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isDrawingBbox) {
        resetDrawingState();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isDrawingBbox]);

  const chartPanelSamples = chartScopeSamples;
  const chartPanelPrimaryLabel = selectedSamples.length > 0
    ? 'Selected Samples'
    : 'Current Map Data';

  return (
    <div ref={mapContainerRef} className="relative w-full h-full">
      <h1 className="sr-only">Volcano map</h1>
      {/* Error Message */}
      {error && error !== dismissedError && (
        // Phones: below the collapsed Map Layers header instead of over it
        <div className="absolute top-[4.5rem] left-20 right-4 sm:top-4 sm:right-auto sm:max-w-md xl:left-1/2 xl:-translate-x-1/2 z-20">
          <ErrorMessage message={error} onDismiss={() => setDismissedError(error)} />
        </div>
      )}

      {/* No Samples Loaded Message */}
      {!hasAppliedFilters && !samplesLoading && samples.length === 0 && showNoSamplesMessage && (
        <div 
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10"
          onClick={(e) => {
            // Close if clicking the backdrop (outside the white box)
            if (e.target === e.currentTarget) {
              setShowNoSamplesMessage(false);
            }
          }}
        >
          <div className="bg-white rounded-xl shadow-2xl border border-line p-6 sm:p-8 w-[calc(100vw-2rem)] max-w-md relative">
            {/* Close button */}
            <button
              onClick={() => setShowNoSamplesMessage(false)}
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 transition-colors"
              title="Dismiss message"
              aria-label="Close message"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            <div className="text-center space-y-4">
              <MapIcon className="w-10 h-10 mx-auto text-volcano-600" aria-hidden="true" />
              <h2 className="text-xl font-bold text-gray-800">No Samples Loaded</h2>
              <p className="text-gray-600">
                To display rock samples on the map, use one of these options:
              </p>
              <ul className="text-left text-gray-700 space-y-2 ml-6">
                <li className="flex items-start">
                  <span className="text-volcano-600 mr-2">•</span>
                  <span><strong>Draw a bounding box</strong> using the spatial search tool on the left</span>
                </li>
                <li className="flex items-start">
                  <span className="text-volcano-600 mr-2">•</span>
                  <span><strong>Apply filters</strong> using the filter button (top-left)</span>
                </li>
                <li className="flex items-start">
                  <span className="text-volcano-600 mr-2">•</span>
                  <span><strong>Select a preset region</strong> from the spatial search widget</span>
                </li>
              </ul>
              <div className="pt-4">
                <button
                  onClick={() => setFilterPanelOpen(true)}
                  className="px-6 py-3 bg-volcano-600 text-white rounded-lg hover:bg-volcano-700 transition-colors font-medium"
                >
                  Open Filters
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Drawing Instructions Overlay */}
      {isDrawingBbox && (
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-20">
          <div className="bg-orange-500 text-white px-6 py-4 rounded-lg shadow-xl">
            <div className="text-center space-y-2">
              <div className="text-lg font-semibold">
                {drawingStart ? 'Click to complete rectangle' : 'Click to start drawing'}
              </div>
              <div className="text-sm">
                {drawingStart
                  ? 'Second click sets the opposite corner'
                  : bboxDrawMode === 'comparison'
                    ? 'First click sets one corner of the comparison bounding box'
                    : 'First click sets one corner of the bounding box'}
              </div>
              <button
                onClick={resetDrawingState}
                className="mt-3 px-4 py-2 bg-white text-orange-600 rounded-md hover:bg-gray-100 font-medium"
              >
                Cancel Drawing (ESC)
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Map Component */}
      <VolcanoMap
        samples={filteredPrimarySamples}
        comparisonSamples={filteredComparisonSamples}
        volcanoes={volcanoes}
        tectonicBoundaries={tectonicBoundaries}
        viewState={viewport}
        showVolcanoes={showVolcanoes}
        showSamplePoints={showSamplePoints}
        showTectonicBoundaries={visibleBoundaryTypes.length > 0}
        selectedVolcanoName={selectedVolcano?.volcano_name}
        selectedVolcanoNumber={selectedVolcano?.volcano_number}
        loading={isLoading}
        onVolcanoClick={handleVolcanoClick}
        onSampleClick={handleSampleClick}
        onViewportChange={handleViewportChange}
        activeBbox={currentBbox}
        comparisonBbox={comparisonMode === 'bbox' ? comparisonBbox : null}
        drawingBboxProp={getDrawingBbox()}
        drawingBboxVariant={bboxDrawMode === 'comparison' ? 'comparison' : 'primary'}
        isDrawingBbox={isDrawingBbox}
        onMapClick={handleMapClick}
        onMapHover={handleMapHover}
      />

      {/* Selection Overlay (for lasso/box selection) */}
      {(selectionMode === 'lasso' || selectionMode === 'box') && mapDimensions.width > 0 && (
        <SelectionOverlay
          mode={selectionMode}
          samples={filteredPrimarySamples}
          onSelectionComplete={handleSelectionComplete}
          onCancel={handleSelectionCancel}
          viewState={viewport}
          width={mapDimensions.width}
          height={mapDimensions.height}
        />
      )}

      {/* Sample Details Panel */}
      <SampleDetailsPanel
        sample={clickedSample}
        onClose={() => setClickedSample(null)}
        onAddToSelection={handleAddToSelection}
        isSelected={clickedSample ? selectedSamples.some(s => s._id === clickedSample._id) : false}
      />

      {/* Summary Stats */}
      <SummaryStats
        samples={filteredPrimarySamples}
        volcanoes={volcanoes}
        selectedSamples={visibleSelectedSamples}
        publicationSamples={chartFilteredSamples}
        publicationSampleScopeCount={chartScopeSamples.length}
        loading={isLoading}
      />

      {/* Layer Controls */}
      <LayerControls
        showVolcanoes={showVolcanoes}
        showSamplePoints={showSamplePoints}
        visibleBoundaryTypes={visibleBoundaryTypes}
        onVolcanoesChange={setShowVolcanoes}
        onSamplePointsChange={setShowSamplePoints}
        onBoundaryTypesChange={setVisibleBoundaryTypes}
      />

      {/* Viewport Controls */}
      <ViewportControls
        viewport={viewport}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        onResetView={handleResetView}
      />

      {/* Left Toolbar Container - Selection & Spatial Search */}
      <div className="absolute top-20 left-4 z-10 flex flex-col gap-4">
        {/* Selection Toolbar */}
        <SelectionToolbar
          mode={selectionMode}
          selectedCount={visibleSelectedSamples.length}
          totalSelectedCount={selectedSamples.length}
          downloadSampleCount={downloadableSamples.length}
          filteredSampleCount={filteredPrimarySamples.length}
          hasBboxFilter={currentBbox !== null}
          onModeChange={setSelectionMode}
          onClearSelection={clearSelection}
          onDownloadSelection={handleDownloadCSV}
          onShowCharts={() => setChartPanelOpen(true)}
        />

        {/* Bbox Search Widget */}
        <BboxSearchWidget
          bbox={currentBbox}
          isDrawing={bboxDrawMode === 'primary'}
          sampleCount={filteredPrimarySamples.length}
          totalSamples={totalSamplesCount}
          onStartDrawing={handleStartDrawing}
          onSetPresetRegion={handleSetPresetRegion}
          onClearBbox={handleClearBbox}
        />
      </div>

      {/* Chart Panel */}
      <ChartPanel
        samples={chartPanelSamples}
        isOpen={chartPanelOpen}
        onToggle={() => setChartPanelOpen(prev => !prev)}
        onClose={() => setChartPanelOpen(false)}
        selectedMatchMethods={selectedMatchMethods}
        onMatchMethodsChange={setSelectedMatchMethods}
        sampleFilters={sampleFilters}
        primaryDatasetLabel={chartPanelPrimaryLabel}
        comparisonBbox={comparisonBbox}
        isDrawingComparisonBbox={isDrawingComparisonBbox}
        onStartComparisonBbox={handleStartComparisonBbox}
        onClearComparisonBbox={handleClearComparisonBbox}
        comparisonMode={comparisonMode}
        onComparisonModeChange={setComparisonMode}
        comparisonVolcano={comparisonVolcano}
        onComparisonVolcanoChange={setComparisonVolcano}
        comparisonSamples={filteredComparisonSamples}
        comparisonLoading={loadingComparisonSamples}
        comparisonError={comparisonSamplesError}
      />

      {/* Filter Button */}
      <button
        onClick={() => setFilterPanelOpen(true)}
        className="absolute top-4 left-4 z-10 bg-white rounded-lg shadow-md border border-line p-4 hover:bg-gray-50 transition-colors duration-200"
        title="Open Filters"
        aria-label="Open filter panel"
      >
        <FilterIcon className="w-5 h-5 text-volcano-600" aria-hidden="true" />
        <span className="sr-only">Filters</span>
      </button>

      {/* Filter Panel */}
      <FilterPanel
        sampleFilters={sampleFilters}
        volcanoFilters={volcanoFilters}
        onSampleFiltersChange={setSampleFilters}
        onVolcanoFiltersChange={setVolcanoFilters}
        isOpen={filterPanelOpen}
        onClose={() => setFilterPanelOpen(false)}
      />

      {/* Loading Overlay */}
      {isLoading && (
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-20">
          <Loader size="lg" text="Loading map data..." />
        </div>
      )}
    </div>
  );
};

export default MapPage;
